import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Typography, useMediaQuery } from '@material-ui/core';
import { Checkbox, Spinner } from 'components/UI';
import { FunctionComponent, useEffect, useState } from 'react';
import Button from 'components/UI/V2/Button/Button';
import Modal from '@material-ui/core/Modal';
import { useApiClientWrapper, useMarketplace } from 'hooks';
import { useDispatch, useSelector } from 'react-redux';
import { PetsState, PetsStatus, printPetCardPDFThunk } from '@spike/pets-action';
import useNonInitialEffect from '@versiondos/hooks';
import { RootState } from 'store';

interface PrintOption {
    id: string;
    label: string;
    checked: boolean;
}

interface PetCardPrintProps {
    petId: number;
    open: boolean;
    className?: string;
    onSaved?: () => void;
    onClose?: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        modal: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            [theme.breakpoints.down('sm')]: {
                inset: 0,
                width: '100%',
                height: '100%',
                zIndex: '10001 !important',
                position: 'absolute !important'
            }
        },
        paper: {
            borderRadius: '24px',
            backgroundColor: '#fff',
            maxWidth: '540px',
            width: '100%',
            padding: '35px 32px 36px',
            [theme.breakpoints.down('sm')]: {
                maxWidth: '100%',
                width: '100%',
                height: '100%',
                margin: 0,
                borderRadius: 0,
                maxHeight: '100%'
            }
        },
        body: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '95%'
        },
        containerTitle: {
            display: 'flex',
            justifyContent: 'space-between',
            [theme.breakpoints.down('sm')]: {
                justifyContent: 'center'
            }
        },
        title: {
            fontWeight: 600,
            fontSize: '18px',
            [theme.breakpoints.down('sm')]: {
                fontSize: '26px !important',
                textAlign: 'center'
            }
        },
        iconClose: {
            fontSize: 20,
            float: 'right',
            cursor: 'pointer',
            [theme.breakpoints.down('sm')]: {
                position: 'absolute',
                top: theme.spacing(2),
                right: theme.spacing(2)
            }
        },
        subTitle: {
            fontWeight: 400,
            fontSize: '15px',
            [theme.breakpoints.down('sm')]: {
                fontSize: '16px !important',
                textAlign: 'center'
            }
        },
        checkboxList: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1.5),
            padding: '10px 0px 25px 0px'
        },
        checkboxLabel: {
            fontWeight: 400,
            fontSize: '16px'
        },
        actions: {
            display: 'flex',
            gap: theme.spacing(1),
            justifyContent: 'flex-end'
        },
        cancelButton: {
            'backgroundColor': '#000',
            'color': '#fff',
            'flex': 1,
            '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.9)'
            }
        },
        printButton: {
            flex: 1
        },
        spinnerStyle: {
            height: '100%'
        }
    })
);

export const PetCardPrint: FunctionComponent<PetCardPrintProps> = props => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const apiClientWrapper = useApiClientWrapper();
    const dispatch = useDispatch();

    const petState = useSelector<RootState, PetsState | undefined>(state => state.pets);

    const [loading, setLoading] = useState(false);
    const [printOptions, setPrintOptions] = useState<PrintOption[]>([
        { id: 'pet-info', label: 'Pet Information', checked: true },
        { id: 'pet-notes', label: 'Pet Notes', checked: false },
        { id: 'vaccines', label: 'Vaccines', checked: false },
        { id: 'medical', label: 'Medical Conditions', checked: false },
        { id: 'vet-info', label: 'Vet Information', checked: false }
    ]);

    const [isAtLeastOneChecked, setIsAtLeastOneChecked] = useState(true);

    useNonInitialEffect(() => {
        if (petState?.status === PetsStatus.PrintPetCardPDFSuccess) {
            setLoading(false);
            handleClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [petState]);

    useEffect(() => {
        setIsAtLeastOneChecked(printOptions.some(option => option.checked));
    }, [printOptions]);

    const handleCheckboxChange = (id: string) => {
        setPrintOptions(
            printOptions.map(option => (option.id === id ? { ...option, checked: !option.checked } : option))
        );
    };

    const handlePrint = async () => {
        setLoading(true);
        await dispatch(printPetCardPDFThunk(apiClientWrapper, props.petId, isMobile, printOptions));
    };

    const handleClose = () => {
        props.onClose && props.onClose();
    };

    return (
        <Modal
            open={props.open}
            onClose={handleClose}
            className={classes.modal}
            aria-labelledby="print-pet-card-modal-title"
            aria-describedby="print-pet-card-modal-description"
        >
            <Box className={classes.paper}>
                {loading ? (
                    <Spinner className={classes.spinnerStyle} />
                ) : (
                    <>
                        <Box className={classes.body}>
                            <Box className={classes.containerTitle}>
                                <Typography className={classes.title}>Print Pet Card</Typography>
                                <FontAwesomeIcon
                                    icon={faTimes}
                                    className={classes.iconClose}
                                    onClick={handleClose}
                                />
                            </Box>
                            <p className={classes.subTitle}>
                                Please select the items you want to print on the pet card.
                            </p>
                            <Box className={classes.checkboxList}>
                                {printOptions.map(option => (
                                    <Box
                                        key={option.id}
                                        className="flex items-center space-x-2"
                                    >
                                        <Checkbox
                                            id={option.id}
                                            label={option.label}
                                            checked={option.checked}
                                            onChange={() => handleCheckboxChange(option.id)}
                                            className={classes.checkboxLabel}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                        <Box className={classes.actions}>
                            <Button
                                label="Cancel"
                                onClick={handleClose}
                                variant="black"
                                size="large"
                                className={classes.cancelButton}
                            />
                            <Button
                                label="Print"
                                onClick={handlePrint}
                                variant="green"
                                size="large"
                                className={classes.printButton}
                                disabled={!isAtLeastOneChecked}
                            />
                        </Box>
                    </>
                )}
            </Box>
        </Modal>
    );
};

export default PetCardPrint;
